import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Download, RefreshCw } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import {
  createUserExport,
  downloadUserExport,
  listUserExports,
  previewUserExport,
  type UserExport,
  type UserSegment,
} from "@/features/users/services/user-export";

const SEGMENTS: { value: UserSegment; label: string; description: string }[] = [
  {
    value: "ACTIVE",
    label: "Active",
    description: "Last session within 30 days",
  },
  {
    value: "INACTIVE",
    label: "Inactive",
    description: "31–90 days, has an active bundle",
  },
  {
    value: "LAPSED",
    label: "Lapsed",
    description: "31–90 days, no active bundle",
  },
  {
    value: "CHURNED",
    label: "Churned",
    description: "90+ days, or never had a session",
  },
];

const STATUS_TONE: Record<
  UserExport["status"],
  "default" | "secondary" | "destructive" | "outline"
> = {
  PENDING: "secondary",
  RUNNING: "secondary",
  READY: "default",
  FAILED: "destructive",
  EXPIRED: "outline",
};

export default function UsersExport() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [searchParams] = useSearchParams();
  const focusedId = searchParams.get("id");

  const [selected, setSelected] = useState<Set<UserSegment>>(
    new Set(SEGMENTS.map((s) => s.value)),
  );

  const preview = useQuery({
    queryKey: ["user-exports", "preview"],
    queryFn: previewUserExport,
    staleTime: 60_000,
  });

  const exportsQuery = useQuery({
    queryKey: ["user-exports", "list"],
    queryFn: listUserExports,
    refetchInterval: (q) => {
      const data = q.state.data as UserExport[] | undefined;
      const hasInFlight = data?.some(
        (e) => e.status === "PENDING" || e.status === "RUNNING",
      );
      return hasInFlight ? 3_000 : false;
    },
  });

  const createMut = useMutation({
    mutationFn: (segments: UserSegment[]) => createUserExport(segments),
    onSuccess: () => {
      toast({
        title: "Export queued",
        description:
          "We'll email you when the CSV is ready. Refresh status below.",
      });
      qc.invalidateQueries({ queryKey: ["user-exports", "list"] });
    },
    onError: (err: any) => {
      toast({
        title: "Could not start export",
        description:
          err?.response?.data?.message ??
          err?.message ??
          "Unknown error — try again.",
        variant: "destructive",
      });
    },
  });

  const totalSelected = useMemo(() => {
    if (!preview.data) return null;
    return Array.from(selected).reduce(
      (sum, seg) => sum + (preview.data?.[seg] ?? 0),
      0,
    );
  }, [preview.data, selected]);

  const toggleSegment = (seg: UserSegment) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(seg)) next.delete(seg);
      else next.add(seg);
      return next;
    });
  };

  const onExport = () => {
    if (selected.size === 0) return;
    createMut.mutate(Array.from(selected));
  };

  const segmentList = Array.from(selected);

  return (
    <div className="flex flex-col gap-6 p-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Export Users</h1>
          <p className="text-sm text-muted-foreground">
            Pick segments, preview the count, and queue a CSV export. The file
            will be emailed to you and listed below for 7 days.
          </p>
        </div>
      </header>

      <Card className="p-5">
        <div className="flex items-center justify-between gap-4 mb-4">
          <h2 className="text-base font-semibold">Segments</h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => preview.refetch()}
            disabled={preview.isFetching}
          >
            <RefreshCw
              className={`mr-2 h-3.5 w-3.5 ${
                preview.isFetching ? "animate-spin" : ""
              }`}
            />
            Refresh counts
          </Button>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          {SEGMENTS.map((seg) => {
            const count = preview.data?.[seg.value];
            const checked = selected.has(seg.value);
            return (
              <label
                key={seg.value}
                className={`flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors ${
                  checked
                    ? "border-orange-500 bg-orange-50/50"
                    : "border-border"
                }`}
              >
                <Checkbox
                  checked={checked}
                  onCheckedChange={() => toggleSegment(seg.value)}
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{seg.label}</span>
                    <span className="text-sm tabular-nums text-muted-foreground">
                      {preview.isLoading
                        ? "…"
                        : count != null
                          ? count.toLocaleString()
                          : "—"}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {seg.description}
                  </p>
                </div>
              </label>
            );
          })}
        </div>

        <div className="mt-5 flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            {totalSelected != null
              ? `${totalSelected.toLocaleString()} users selected`
              : "Loading counts…"}
          </div>
          <Button
            onClick={onExport}
            disabled={
              selected.size === 0 ||
              createMut.isPending ||
              !preview.data ||
              totalSelected === 0
            }
          >
            {createMut.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Export {segmentList.length === 4 ? "all" : segmentList.length}{" "}
            segment{segmentList.length === 1 ? "" : "s"}
          </Button>
        </div>
      </Card>

      <Card className="p-5">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-semibold">Recent exports</h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => exportsQuery.refetch()}
            disabled={exportsQuery.isFetching}
          >
            <RefreshCw
              className={`mr-2 h-3.5 w-3.5 ${
                exportsQuery.isFetching ? "animate-spin" : ""
              }`}
            />
            Refresh
          </Button>
        </div>

        {exportsQuery.isLoading ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : !exportsQuery.data || exportsQuery.data.length === 0 ? (
          <div className="text-sm text-muted-foreground">
            No exports yet. Queue your first one above.
          </div>
        ) : (
          <div className="flex flex-col divide-y">
            {exportsQuery.data.map((row) => (
              <ExportRow
                key={row.id}
                row={row}
                highlight={row.id === focusedId}
              />
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function DownloadButton({ id }: { id: string }) {
  const [loading, setLoading] = useState(false);

  const handleClick = async () => {
    setLoading(true);
    try {
      await downloadUserExport(id);
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      onClick={handleClick}
      disabled={loading}
      className="inline-flex items-center gap-2 rounded-md border border-input bg-background px-3 py-1.5 text-sm hover:bg-accent disabled:opacity-50"
    >
      {loading ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      ) : (
        <Download className="h-3.5 w-3.5" />
      )}
      {loading ? "Downloading…" : "Download"}
    </button>
  );
}

function ExportRow({
  row,
  highlight,
}: {
  row: UserExport;
  highlight?: boolean;
}) {
  const created = new Date(row.created_at).toLocaleString();
  const expires = row.expires_at
    ? new Date(row.expires_at).toLocaleDateString()
    : null;
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (highlight && ref.current) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [highlight]);

  return (
    <div
      ref={ref}
      className={`flex flex-wrap items-center justify-between gap-3 py-3 first:pt-0 last:pb-0 ${
        highlight ? "rounded-md ring-2 ring-orange-400 ring-offset-2" : ""
      }`}
    >
      <div className="flex flex-col gap-0.5">
        <div className="flex items-center gap-2">
          <Badge variant={STATUS_TONE[row.status]}>{row.status}</Badge>
          <span className="text-sm font-medium">
            {row.segments.join(", ") || "—"}
          </span>
          {row.row_count != null && (
            <span className="text-xs tabular-nums text-muted-foreground">
              {row.row_count.toLocaleString()} rows
            </span>
          )}
        </div>
        <div className="text-xs text-muted-foreground">
          Created {created}
          {expires ? ` • expires ${expires}` : ""}
          {row.error ? ` • error: ${row.error}` : ""}
        </div>
      </div>

      {row.status === "READY" ? (
        <DownloadButton id={row.id} />
      ) : row.status === "RUNNING" || row.status === "PENDING" ? (
        <span className="inline-flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Generating…
        </span>
      ) : null}
    </div>
  );
}
